import React, { useEffect, useState, useRef } from 'react';
import { Loader } from 'lucide-react';
import SeatsioDesignerWidget from '../../../components/SeatsioDesignerWidget';
import { seatMapApi } from '../../../services/api';

const ChartDesignStep = ({
  formData,
  chartKey,
  ticketTypes,
  onDesignComplete,
  handleInputChange,
  onCancelDesign
}) => {
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState(null);
  const processingRef = useRef(false);

  // Helper to generate categories from ticket types
  const createCategoriesFromTickets = (tickets) => {
    const colors = ['#E63946', '#F1FAEE', '#A8DADC', '#457B9D', '#1D3557', '#F4A261', '#2A9D8F', '#E76F51'];
    return tickets.map((ticket, index) => ({
      key: `${index + 1}`, // Cast to string for backend compatibility
      label: ticket.name,
      color: colors[index % colors.length],
      accessible: false // Default to false, can be updated in designer
    }));
  };

  useEffect(() => {
    const initializeChart = async () => {
      // If we have a chart key, but it's not "configured" for this event yet
      // OR if we entered with a key that we know is a template (managed via chartConfigured flag)
      if (chartKey && !formData.chartConfigured && !processingRef.current) {
        processingRef.current = true;
        setInitializing(true);
        setError(null);

        try {
          console.log('Initializing chart from template:', chartKey);

          // 1. Duplicate the template chart
          // If the key is 'new' or empty, we create brand new. 
          // But here chartKey should be the template selected in previous step.
          // If chartKey matches a known template, duplicate it.
          // If we are editing, chartKey might already be the event chart.
          // The flag chartConfigured helps us know if we need to do this.

          let targetChartKey = chartKey;

          // Only duplicate if we are "configuring" (i.e. starting from a template)
          // If the user selected "Create New" (empty key), we create a new one.
          // If the user selected a Template, we duplicate it.

          let newChart = null;

          if (chartKey === 'new' || !chartKey) {
            // Should not happen if filtered correctly, but handle creaing new
            const response = await seatMapApi.createChart(formData.title + ' Chart');
            newChart = response.data;
          } else if (formData.useSharedChart) {
            // USE SHARED: Do not duplicate. Use the key as is.
            console.log('Using shared chart (no duplication):', chartKey);
            // Mock a response object since we don't have a new chart object
            newChart = { key: chartKey };
          } else {
            // DUPLICATE (Default)
            const response = await seatMapApi.duplicateChart(chartKey, formData.title + ' - Seating Chart');
            newChart = response.data;
          }

          targetChartKey = newChart.key;
          console.log('Created new chart instance:', targetChartKey);

          // 2. Import Ticket Types as Categories
          if (ticketTypes && ticketTypes.length > 0) {
            const categories = createCategoriesFromTickets(ticketTypes);
            await seatMapApi.updateChartCategories(targetChartKey, categories);
            console.log('Imported categories:', categories);
          }

          // 3. Update Form Data
          // We update the chart key to the NEW instance and mark as configured
          handleInputChange('seatsIoChartKey', targetChartKey);
          handleInputChange('chartConfigured', true);

        } catch (err) {
          console.error('Failed to initialize chart:', err);
          setError('Failed to setup seating chart. Please try again.');
        } finally {
          setInitializing(false);
          processingRef.current = false;
        }
      }
    };

    initializeChart();
  }, [chartKey, formData.chartConfigured, formData.title, formData.useSharedChart, ticketTypes, handleInputChange]);

  if (initializing) {
    return (
      <div className="form-section centered" style={{ minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Loader className="spinner" size={48} />
        <h3 style={{ marginTop: '20px' }}>Setting up your seating chart...</h3>
        <p className="text-muted">Duplicating template and importing ticket types.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-section">
        <div className="wizard-callout danger">
          <h3>Error</h3>
          <p>{error}</p>
          <button className="secondary-btn" onClick={onCancelDesign}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-section" style={{ height: '80vh', padding: 0 }}>
      {/* We pass the key. If it was just updated, it will be the new key. 
          If already configured, it's the existing key. */}
      {chartKey && (
        <SeatsioDesignerWidget
          chartKey={chartKey}
          eventTitle={formData.title}
          onDesignComplete={onDesignComplete}
          onCancel={onCancelDesign}
        />
      )}

      {!chartKey && (
        <div className="wizard-callout danger">
          No chart selected. Please go back to Venue Setup.
          <button className="secondary-btn" onClick={onCancelDesign}>Back</button>
        </div>
      )}
    </div>
  );
};

export default ChartDesignStep;
